import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { extendSession, getSession, stopSession } from '@/lib/api/sessions'
import type { ApiError } from '@/lib/api/client'

const EXTEND_OPTIONS = [30, 60, 120] as const

function formatRemaining(ms: number): string {
  if (ms <= 0) return '00:00'
  const total = Math.floor(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

function formatUzs(tiyin: number): string {
  return `${(tiyin / 100).toLocaleString('ru-RU')} so'm`
}

export function SessionDetailPage() {
  const { id } = useParams({ from: '/sessions/$id' })
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data, error, isLoading } = useQuery({
    queryKey: ['session', id],
    queryFn: () => getSession(id),
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'pending_payment' ? 3_000 : 30_000
    },
  })

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [])

  const [extendMin, setExtendMin] = useState<(typeof EXTEND_OPTIONS)[number]>(60)
  const [actionError, setActionError] = useState<string | null>(null)

  const extendMut = useMutation({
    mutationFn: () => extendSession(id, extendMin),
    onSuccess: (res) => {
      if (res.paymentUrl) {
        window.location.href = res.paymentUrl
      } else {
        qc.invalidateQueries({ queryKey: ['session', id] })
      }
    },
    onError: (err) => setActionError((err as unknown as ApiError).message ?? 'error'),
  })

  const stopMut = useMutation({
    mutationFn: () => stopSession(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session', id] })
    },
    onError: (err) => setActionError((err as unknown as ApiError).message ?? 'error'),
  })

  if (isLoading) {
    return <Section><p className="text-slate-600">{t('session.loading')}</p></Section>
  }
  if (error || !data) {
    return (
      <Section>
        <p className="text-rose-600">{t('session.not_found')}</p>
        <Link to="/map" className="mt-4 inline-block text-sky-700 underline">
          {t('session.back_to_map')}
        </Link>
      </Section>
    )
  }

  const expiresMs = new Date(data.expiresAt).getTime()
  const remaining = expiresMs - now
  const isActive = data.status === 'active'
  const isPending = data.status === 'pending_payment'
  const isEnded = data.status === 'expired' || data.status === 'cancelled'

  return (
    <Section>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{t('session.zone')}</p>
          <h1 className="text-2xl font-semibold">
            {data.zone.code} — {data.zone.name}
          </h1>
          <p className="text-sm text-slate-600">{data.zone.city}</p>
        </div>
        <StatusBadge status={data.status} />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t('session.plate')} value={data.plate} mono />
          <Field label={t('session.amount')} value={formatUzs(data.amount)} />
          <Field
            label={isEnded ? t('session.ended_at') : t('session.expires_in')}
            value={
              isEnded
                ? new Date(data.expiresAt).toLocaleString()
                : formatRemaining(remaining)
            }
            highlight={isActive && remaining < 5 * 60_000}
          />
        </div>

        {isPending && (
          <div className="mt-6 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {t('session.pending_payment')}
          </div>
        )}

        {isActive && (
          <div className="mt-8 space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium">{t('session.extend')}</p>
              <div className="flex flex-wrap gap-2">
                {EXTEND_OPTIONS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setExtendMin(m)}
                    className={
                      'rounded-lg border px-4 py-2 text-sm font-medium ' +
                      (extendMin === m
                        ? 'border-sky-600 bg-sky-50 text-sky-700'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400')
                    }
                  >
                    +{m} {t('session.min')}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={extendMut.isPending}
                onClick={() => {
                  setActionError(null)
                  extendMut.mutate()
                }}
                className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
              >
                {extendMut.isPending ? t('session.submitting') : t('session.extend_pay')}
              </button>
              <button
                type="button"
                disabled={stopMut.isPending}
                onClick={() => {
                  setActionError(null)
                  if (window.confirm(t('session.stop_confirm'))) stopMut.mutate()
                }}
                className="rounded-lg border border-rose-300 bg-white px-5 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                {stopMut.isPending ? t('session.submitting') : t('session.stop_now')}
              </button>
            </div>
            {actionError && <p className="text-sm text-rose-600">{actionError}</p>}
          </div>
        )}

        {isEnded && (
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => navigate({ to: '/map' })}
              className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700"
            >
              {t('session.start_new')}
            </button>
          </div>
        )}
      </div>
    </Section>
  )
}

function Section({ children }: { children: React.ReactNode }) {
  return <section className="mx-auto max-w-3xl px-4 py-10">{children}</section>
}

function Field({
  label,
  value,
  mono,
  highlight,
}: {
  label: string
  value: string
  mono?: boolean
  highlight?: boolean
}) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p
        className={
          'mt-1 text-lg font-semibold ' +
          (mono ? 'font-mono ' : '') +
          (highlight ? 'text-rose-600' : 'text-slate-900')
        }
      >
        {value}
      </p>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  const cls: Record<string, string> = {
    pending_payment: 'bg-amber-100 text-amber-800',
    active: 'bg-emerald-100 text-emerald-800',
    expired: 'bg-slate-200 text-slate-700',
    cancelled: 'bg-slate-200 text-slate-700',
  }
  return (
    <span
      className={
        'rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ' +
        (cls[status] ?? cls.expired)
      }
    >
      {t(`session.status.${status}`)}
    </span>
  )
}
